import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AnimatePresence,
  animate,
  motion,
  type MotionValue,
  useDragControls,
  useMotionValue,
  useReducedMotion,
} from "motion/react";
import { cn } from "../ui/utils";
import { AssistantMascot } from "./components/AssistantMascot";
import { ChatPanel } from "./components/ChatPanel";
import { useComplianceAssistantStore } from "./store";

/**
 * Chatbot-scoped styling injected once with the widget so the module stays fully
 * self-contained (no edits to global stylesheets). Every selector is prefixed
 * `ca-` and cannot affect anything outside the Compliance Companion.
 *
 * - ca-thinking-shimmer: a soft sheen swept through the "thinking" status text
 *   so the wait reads as a live, premium state instead of a static label.
 * - ca-hover-reveal / ca-msg: message actions and timestamps stay out of the way
 *   on pointer devices and appear on hover/focus, but are always visible on
 *   touch devices (no hover) so they are never unreachable.
 */
const CA_STYLES = `
.ca-thinking-shimmer{
  background:linear-gradient(90deg,rgb(100 116 139 / .85) 0%,rgb(79 70 229) 45%,rgb(100 116 139 / .85) 90%);
  background-size:200% auto;background-clip:text;-webkit-background-clip:text;color:transparent;
  animation:ca-text-shimmer 2.4s linear infinite;
}
.dark .ca-thinking-shimmer{
  background:linear-gradient(90deg,rgb(159 173 196 / .8) 0%,rgb(196 181 253) 45%,rgb(159 173 196 / .8) 90%);
  background-size:200% auto;background-clip:text;-webkit-background-clip:text;color:transparent;
}
@keyframes ca-text-shimmer{0%{background-position:200% center}100%{background-position:0% center}}
.ca-hover-reveal{opacity:1;transition:opacity .2s ease}
@media (hover:hover) and (pointer:fine){
  .ca-hover-reveal{opacity:0}
  .ca-msg:hover .ca-hover-reveal,.ca-hover-reveal:focus-within{opacity:1}
}
@media (prefers-reduced-motion:reduce){
  .ca-thinking-shimmer{animation:none}
  .ca-hover-reveal{transition:none}
}
`;

export function ComplianceAssistant() {
  const open = useComplianceAssistantStore((s) => s.open);
  const minimized = useComplianceAssistantStore((s) => s.minimized);
  const openPanel = useComplianceAssistantStore((s) => s.openPanel);

  const reduceMotion = useReducedMotion();
  const launcherRef = useRef<HTMLButtonElement>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const lastCompactX = useRef(0);
  const lastCompactY = useRef(0);
  const dragControls = useDragControls();
  const [dragConstraints, setDragConstraints] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

  // Draggable launcher bubble: its own persisted offset from the anchor plus a
  // viewport-clamp box so it can be dragged anywhere but never off-screen.
  const launcherX = useMotionValue(0);
  const launcherY = useMotionValue(0);
  const launcherDragged = useRef(false);
  const launcherWrapRef = useRef<HTMLDivElement>(null);
  const [launcherConstraints, setLauncherConstraints] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (open) return; // launcher is only mounted while the chat is closed
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const recompute = () => {
      const el = launcherWrapRef.current;
      if (!el) return;
      const w = el.offsetWidth; // offset* ignore the drag transform, so it stays stable
      const h = el.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const RIGHT = vw >= 640 ? 24 : 20; // matches sm:right-6 / right-5
      const BOTTOM = 96; // matches bottom-24
      const M = 8; // keep at least this much viewport margin
      const left = -Math.max(0, vw - RIGHT - w - M);
      const right = Math.max(0, RIGHT - M);
      const top = -Math.max(0, vh - BOTTOM - h - M);
      const bottom = Math.max(0, BOTTOM - M);
      setLauncherConstraints({ top, bottom, left, right });
      // Re-clamp if the viewport shrank while the bubble sat off to one side.
      launcherX.set(clamp(launcherX.get(), left, right));
      launcherY.set(clamp(launcherY.get(), top, bottom));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
    // `mounted` matters: the widget renders null until it is true, so the ref is
    // only attached on the second pass — recompute then or constraints stay 0.
  }, [open, mounted, launcherX, launcherY]);

  // Return focus to the launcher when the panel closes so keyboard users are not
  // stranded on a removed element. Only on the open->closed transition, never on
  // the initial page load.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) launcherRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (open) {
      x.set(0);
      y.set(0);
      lastCompactX.current = 0;
      lastCompactY.current = 0;
      // Send the launcher bubble home when the chat opens, so wherever it was
      // dragged it returns to its initial position for the next appearance and
      // the chat window always opens from the standard bottom-right anchor.
      if (reduceMotion) {
        launcherX.set(0);
        launcherY.set(0);
      } else {
        animate(launcherX, 0, { type: "spring", stiffness: 420, damping: 34 });
        animate(launcherY, 0, { type: "spring", stiffness: 420, damping: 34 });
      }
    }
  }, [open, x, y, launcherX, launcherY, reduceMotion]);

  useEffect(() => {
    const settle = (mv: MotionValue<number>, target: number) => {
      if (target === mv.get()) return;
      if (reduceMotion) mv.set(target);
      else animate(mv, target, { type: "spring", stiffness: 500, damping: 44 });
    };
    const recompute = () => {
      if (!minimized) return;
      // Motion values offset from the fixed bottom-right anchor: 0 = anchored,
      // negative = moved up / left. Clamp so the compact card can travel across
      // the screen but never leaves the viewport.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const MARGIN = 8;
      const SIDE_GAP = 16; // matches the `bottom-4 right-4` anchor
      const width = Math.min(400, vw - 32);
      const height = Math.min(vh * 0.6, 500);
      const top = Math.min(0, MARGIN - (vh - SIDE_GAP - height));
      const left = Math.min(0, MARGIN - (vw - SIDE_GAP - width));
      setDragConstraints({ top, bottom: 0, left, right: 0 });
      settle(x, Math.max(left, Math.min(0, lastCompactX.current)));
      settle(y, Math.max(top, Math.min(0, lastCompactY.current)));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [minimized, reduceMotion, x, y]);

  if (!mounted || typeof document === "undefined") return null;

  const sizeClass = "h-[min(500px,60dvh)] w-[min(400px,calc(100vw-2rem))]";

  return createPortal(
    <>
      <style>{CA_STYLES}</style>
      <AnimatePresence>
        {!open && (
          <motion.div
            key="ca-launcher"
            ref={launcherWrapRef}
            drag
            dragMomentum={false}
            dragElastic={0.08}
            dragConstraints={launcherConstraints}
            style={{ x: launcherX, y: launcherY }}
            onPointerDownCapture={() => {
              launcherDragged.current = false;
            }}
            onDragStart={() => {
              launcherDragged.current = true;
              document.body.style.cursor = "grabbing";
            }}
            onDragEnd={() => {
              document.body.style.cursor = "";
            }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className="group fixed bottom-24 right-5 z-[60] cursor-grab touch-none active:cursor-grabbing sm:right-6"
          >
            <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-lg border border-border bg-popover px-3 py-1.5 text-[12px] font-medium text-popover-foreground opacity-0 shadow-lg transition-all duration-200 group-hover:opacity-100 group-hover:-translate-x-0.5">
              Ask the Compliance Companion
            </span>
            <motion.button
              ref={launcherRef}
              type="button"
              onClick={() => {
                // A drag ends with a click event too; swallow it so dragging the
                // bubble doesn't also open the chat. A plain tap still opens it.
                if (launcherDragged.current) {
                  launcherDragged.current = false;
                  return;
                }
                openPanel();
              }}
              aria-label="Open Compliance Companion"
              whileHover={{ scale: 1.12 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 16 }}
              className="relative block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <motion.span
                aria-hidden
                className="pointer-events-none absolute -inset-1.5 rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.5),transparent_70%)] blur-lg"
                animate={reduceMotion ? undefined : { opacity: [0.4, 0.68, 0.4], scale: [1, 1.08, 1] }}
                transition={
                  reduceMotion
                    ? undefined
                    : { duration: 3.4, repeat: Infinity, ease: "easeInOut" }
                }
              />
              <span className="pointer-events-none absolute inset-0 rounded-full bg-violet-500/25 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-90" />
              <span className="relative block drop-shadow-[0_6px_14px_rgba(124,58,237,0.45)] transition-[filter] duration-300 group-hover:drop-shadow-[0_10px_24px_rgba(139,92,246,0.6)]">
                <AssistantMascot className="size-14 sm:size-16" />
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && minimized && (
          <motion.aside
            key="ca-panel"
            drag={minimized}
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={dragConstraints}
            dragMomentum={false}
            dragElastic={0.06}
            onDragStart={() => {
              document.body.style.cursor = "grabbing";
            }}
            onDragEnd={() => {
              document.body.style.cursor = "";
              lastCompactX.current = x.get();
              lastCompactY.current = y.get();
            }}
            style={{ x, y }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            whileDrag={reduceMotion ? undefined : { scale: 1.02 }}
            transition={{ type: "tween", ease: [0.4, 0, 0.2, 1], duration: 0.28 }}
            className={cn(
              "ca-panel-glow fixed bottom-4 right-4 z-[60] flex origin-bottom-right flex-col overflow-hidden rounded-2xl border border-border transition-[width,height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] dark:border-violet-400/40",
              sizeClass,
            )}
            role="dialog"
            aria-label="Compliance Companion"
          >
            <ChatPanel dragControls={dragControls} />
          </motion.aside>
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
}
